const KEY = "marketlink.navTrail";
const TAP_KEY = "marketlink.tapTrail";
const MAX_ENTRIES = 20;
const MAX_TAPS = 10;

export type NavEntry = {
  path: string;
  at: number;
};

export type TapEntry = {
  tag: string;
  text: string;
  x: number;
  y: number;
  at: number;
};

function read(): NavEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(trail: NavEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(trail));
  } catch {
    /* sessionStorage full / disabled — ignore */
  }
}

export function pushNavEntry(path: string) {
  const trail = read();
  const last = trail[trail.length - 1];
  if (last && last.path === path) return;
  trail.push({ path, at: Date.now() });
  if (trail.length > MAX_ENTRIES) trail.splice(0, trail.length - MAX_ENTRIES);
  write(trail);
}

export function getNavTrail(): NavEntry[] {
  return read();
}

export function clearNavTrail() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

function readTaps(): TapEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(TAP_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeTaps(taps: TapEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(TAP_KEY, JSON.stringify(taps));
  } catch {
    /* ignore */
  }
}

export function pushTapEntry(e: MouseEvent | PointerEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const tag = target.tagName?.toLowerCase() ?? "?";
  const text = (
    target.getAttribute("aria-label") ||
    target.textContent ||
    ""
  ).trim().slice(0, 40);
  const taps = readTaps();
  taps.push({ tag, text, x: Math.round(e.clientX), y: Math.round(e.clientY), at: Date.now() });
  if (taps.length > MAX_TAPS) taps.splice(0, taps.length - MAX_TAPS);
  writeTaps(taps);
}

export function getTapTrail(): TapEntry[] {
  return readTaps();
}

export function parseDeviceFromUA(ua: string): string {
  const android = ua.match(/Android\s+([\d.]+)/);
  const model = ua.match(/;\s*([^;)]+)\s+Build\//);
  const ios = ua.match(/iPhone OS ([\d_]+)/);
  const ipad = ua.match(/iPad.*OS ([\d_]+)/);
  if (android) {
    const modelName = model?.[1]?.trim() ?? "Android device";
    return `Android ${android[1]} (${modelName})`;
  }
  if (ios) return `iOS ${ios[1].replace(/_/g, ".")} (iPhone)`;
  if (ipad) return `iPadOS ${ipad[1].replace(/_/g, ".")}`;
  if (ua.includes("Macintosh")) return "macOS";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Linux")) return "Linux";
  return "Unknown";
}

export function cleanPath(path: string): string {
  return path.replace(/[?&]_cb=\d+/, "").replace(/\?$/, "");
}
